import React from 'react'
import { cn } from './cn'
import { mergeAriaDescribedBy, mergeAriaLabelledBy, useFieldA11y } from './field'

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  autoGrow?: boolean
  maxRows?: number
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    autoGrow = true,
    maxRows = 12,
    className,
    onInput,
    style,
    id,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    'aria-labelledby': ariaLabelledBy,
    value,
    ...rest
  },
  forwardedRef
) {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)
  /** The width the current height was measured at; null until a real measure. */
  const measuredWidthRef = React.useRef<number | null>(null)
  const generatedId = React.useId()
  const { controlId, invalid: fieldInvalid, labelId, descriptionIds } = useFieldA11y()

  React.useImperativeHandle(forwardedRef, () => textareaRef.current as HTMLTextAreaElement)

  const resize = React.useCallback(() => {
    const textarea = textareaRef.current
    if (!autoGrow || !textarea) return
    textarea.style.height = 'auto'
    // Not laid out yet (inside a <dialog> that opens in its own layout effect,
    // which runs after this one): there is nothing to measure, and writing the
    // 0 would pin the box at its padding. `rows` keeps it at its natural height
    // until the observer below sees it appear.
    if (textarea.scrollHeight === 0) {
      textarea.style.height = ''
      measuredWidthRef.current = null
      return
    }
    measuredWidthRef.current = textarea.clientWidth
    const computed = window.getComputedStyle(textarea)
    const fontSize = Number.parseFloat(computed.fontSize) || 14
    const lineHeight = Number.parseFloat(computed.lineHeight) || fontSize * 1.5
    const verticalPadding =
      (Number.parseFloat(computed.paddingTop) || 0) +
      (Number.parseFloat(computed.paddingBottom) || 0)
    const maxHeight = lineHeight * maxRows + verticalPadding
    const nextHeight = Math.min(textarea.scrollHeight, maxHeight)
    textarea.style.height = `${nextHeight}px`
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden'
  }, [autoGrow, maxRows])

  React.useLayoutEffect(() => {
    resize()
  }, [resize, value])

  // Measure again whenever the width differs from the one the height was
  // measured at: the first layout after it was mounted hidden, or a narrower
  // window rewrapping the text. A box already measured at mount is left alone,
  // and height changes never count, so the height this sets cannot loop.
  React.useEffect(() => {
    const textarea = textareaRef.current
    if (!autoGrow || !textarea || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      if (textarea.clientWidth === measuredWidthRef.current) return
      resize()
    })
    observer.observe(textarea)
    return () => observer.disconnect()
  }, [autoGrow, resize])

  return (
    <textarea
      ref={textareaRef}
      id={id ?? controlId ?? generatedId}
      value={value}
      aria-invalid={ariaInvalid ?? (fieldInvalid || undefined)}
      aria-describedby={mergeAriaDescribedBy(ariaDescribedBy, descriptionIds)}
      aria-labelledby={mergeAriaLabelledBy(ariaLabelledBy, labelId)}
      className={cn('input resize-none leading-normal', className)}
      style={{ ...style, overflowY: style?.overflowY ?? 'hidden' }}
      onInput={(event) => {
        resize()
        onInput?.(event)
      }}
      {...rest}
    />
  )
})
